import type { AxisSelection } from '@atlas/types'
import { AxisCode, ClassifiedObjectType } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Message } from '@/domain/message.types'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { classificationApi } from '@/services/classification.service'
import { messageApi } from '@/services/message.service'

/** Root of the work-order-messages key family. Exported so other hooks and the realtime query bridge invalidate the same key. */
export const WORK_ORDER_MESSAGES_KEY = 'work-order-messages'

/** Query key for a work order's messages (discovered by order_code). */
const messagesKey = (orderCode: string | null) => [WORK_ORDER_MESSAGES_KEY, orderCode]

/**
 * The messages linked to a work order — discovered by the `order_code`
 * attribute the import flow stamps on each message (the same mechanism the
 * files panel uses for deliverables). `order_code` is an `object_attribute`
 * (free-text), NOT a dimension, so it is passed as an ATTRIBUTE filter (the
 * dimension `filters` arg stays empty). The classification search returns bare
 * message refs; we hydrate each to its full record for display.
 *
 * A different question from `useBrandMessages`: that reads the brand's message
 * LIBRARY and narrows it by a tuple, which answers "what copy exists for this
 * brand". This answers "what copy does this order have" — the per-frame carousel
 * text a producer wrote, imported or linked for it. It is also the same link the
 * validation-asset gate and the PDF render resolve copy through, so this read and
 * the render agree on what the order has.
 *
 * Kept out of the generic `['messages']` cache so the global list and this
 * order-scoped view invalidate independently.
 *
 * Hydration does NOT swallow per-message failures: a failed `get` rejects the
 * whole query so react-query reports `isError` — a network/API failure must not
 * masquerade as "the order has no messages" (which is what silently dropping the
 * failed refs would do). An order's copy is a bounded set, so there is no
 * unbounded fan-out here to window the way the brand page's library read needs.
 */
export const useWorkOrderMessages = (orderCode: string | null) =>
	useQuery<Message[]>({
		queryKey: messagesKey(orderCode),
		queryFn: async () => {
			const refs = await classificationApi.search(ClassifiedObjectType.MESSAGE, {}, {
				[AxisCode.ORDER_CODE]: [orderCode as string],
			} as AxisSelection)
			return Promise.all([...new Set(refs.map((r) => r.objectId))].map((id) => messageApi.get(id)))
		},
		enabled: !!orderCode,
	})

/**
 * Edit one message's copy in place, in the order's list.
 *
 * Optimistic, per the project's mutation rule: the row is patched in the cached
 * list on `onMutate` so the edit shows immediately, restored on ANY error, and
 * invalidated on settle so the server stays authoritative. `onError` is not
 * branched on a status code — a value the write did not commit must never stay on
 * screen, whatever the reason.
 */
export const useUpdateWorkOrderMessage = (orderCode: string | null) => {
	const qc = useQueryClient()
	const key = messagesKey(orderCode)
	return useMutation({
		mutationFn: ({ messageId, content }: { messageId: string; content: string }) =>
			messageApi.update(messageId, { content }),
		onMutate: async ({ messageId, content }) => {
			await qc.cancelQueries({ queryKey: key })
			const previous = qc.getQueryData<Message[]>(key)
			qc.setQueryData<Message[]>(key, (current) =>
				(current ?? []).map((message) => (message.messageId === messageId ? { ...message, content } : message)),
			)
			return { previous }
		},
		onError: (error, _variables, context) => {
			if (context?.previous) qc.setQueryData(key, context.previous)
			notification.error(apiErrorMessage(error, 'Could not save the copy.'))
		},
		onSettled: () => void qc.invalidateQueries({ queryKey: key }),
	})
}
